import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function CostStatementEmailDialog({ open, onOpenChange, clientId, clientName, clientEmail }) {
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState("billable");
  const [manual, setManual] = useState("");
  const [options, setOptions] = useState([]); // {label, email, group}
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject(`Cost statement — ${clientName || "case"}`);
    setBody(""); setCc([]); setScope("billable"); setManual("");
    setTo(clientEmail ? [clientEmail] : []);
    Promise.all([
      api.get("/messages/contacts").then((r) => r.data).catch(() => []),
      api.get("/contacts").then((r) => r.data).catch(() => []),
    ]).then(([team, book]) => {
      const opts = [];
      if (clientEmail) opts.push({ label: `${clientName} (client)`, email: clientEmail, group: "Client" });
      team.forEach((t) => { if (t.email) opts.push({ label: t.name, email: t.email, group: "Team" }); });
      book.forEach((c) => { if (c.email) opts.push({ label: `${c.name} · ${c.type}`, email: c.email, group: "Address book" }); });
      setOptions(opts);
    });
  }, [open, clientEmail, clientName]);

  const addTo = (email, list, setList) => {
    if (email && !list.includes(email)) setList([...list, email]);
  };
  const remove = (email, list, setList) => setList(list.filter((e) => e !== email));

  const addManual = () => {
    const e = manual.trim();
    if (!isEmail(e)) { toast.error("Enter a valid email address"); return; }
    addTo(e, to, setTo); setManual("");
  };

  const send = async () => {
    if (to.length === 0) { toast.error("Add at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/costs/email`, { to, cc, subject, body, scope });
      toast.success(`Statement emailed to ${data.recipients.length} recipient${data.recipients.length === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send statement");
    } finally { setSending(false); }
  };

  const Chip = ({ email, list, setList }) => (
    <span className="inline-flex items-center gap-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold px-2 py-1 rounded-sm">
      {email}
      <button onClick={() => remove(email, list, setList)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="cost-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Email cost statement</DialogTitle>
          <DialogDescription className="sr-only">Email the PDF cost statement to a client, team member or contact.</DialogDescription>
        </DialogHeader>

        <label className={labelCls}>Statement scope</label>
        <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="cost-email-scope" className={inputCls + " mb-3"}>
          <option value="billable">Billable costs only</option>
          <option value="all">All costs</option>
        </select>

        <label className={labelCls}>Pick a recipient</label>
        <select data-testid="cost-email-recipient-select" value="" onChange={(e) => { addTo(e.target.value, to, setTo); }}
          className={inputCls + " mb-2"}>
          <option value="">Select client, team member or contact…</option>
          {["Client", "Team", "Address book"].map((grp) => {
            const items = options.filter((o) => o.group === grp);
            if (!items.length) return null;
            return (
              <optgroup key={grp} label={grp}>
                {items.map((o, i) => <option key={`${grp}-${i}`} value={o.email}>{o.label} — {o.email}</option>)}
              </optgroup>
            );
          })}
        </select>

        <div className="flex gap-2 mb-2">
          <input data-testid="cost-email-manual" value={manual} onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
            placeholder="Or type an email address" className={inputCls} />
          <button onClick={addManual} data-testid="cost-email-add-manual"
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-3 rounded-sm"><Plus className="w-4 h-4" /></button>
        </div>

        <p className="text-xs font-semibold text-gray-500 mb-1">To</p>
        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]" data-testid="cost-email-to-chips">
          {to.length === 0 && <span className="text-xs text-gray-400">No recipients yet</span>}
          {to.map((e) => <Chip key={e} email={e} list={to} setList={setTo} />)}
        </div>

        <label className={labelCls}>Cc (optional)</label>
        <select data-testid="cost-email-cc-select" value="" onChange={(e) => addTo(e.target.value, cc, setCc)} className={inputCls + " mb-1"}>
          <option value="">Add a Cc recipient…</option>
          {options.map((o) => <option key={`cc-${o.group}-${o.email}`} value={o.email}>{o.label} — {o.email}</option>)}
        </select>
        <div className="flex flex-wrap gap-1.5 mb-3">{cc.map((e) => <Chip key={e} email={e} list={cc} setList={setCc} />)}</div>

        <label className={labelCls}>Subject</label>
        <input data-testid="cost-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls + " mb-3"} />

        <label className={labelCls}>Message</label>
        <textarea data-testid="cost-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder="Leave blank to use a default cover message." className={inputCls} />

        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="cost-email-send-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {sending ? "Sending…" : "Send statement"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
